import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

function useInView(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const offers = [
  {
    id: "assess",
    index: "01",
    title: "Assess and Orientate",
    lead: "You have a specific opportunity or market entry question.",
    body: "We assess whether it is real, worth pursuing, and how to approach it. You get a decision pack and a practical plan.",
    duration: "Typically 1 to 3 weeks",
    icon: "ri-compass-3-line",
  },
  {
    id: "position",
    index: "02",
    title: "Position and Pursue",
    lead: "You have identified a target programme, framework, or buyer.",
    body: "We build the pursuit architecture: stakeholder mapping, competitive positioning, engagement sequencing, and a clear objective for every interaction.",
    duration: "Typically 4 to 8 weeks",
    icon: "ri-funds-line",
  },
  {
    id: "sustain",
    index: "03",
    title: "Sustain and Advance",
    lead: "You have multiple live opportunities and need sustained advisory support.",
    body: "Monthly prioritisation, blocker resolution, stakeholder continuity, and decision support. We act as a dedicated UK market advisory function without the permanent headcount.",
    duration: "Ongoing monthly retainer",
    icon: "ri-radar-line",
  },
];

const audiences = [
  {
    title: "Proven innovators",
    icon: "ri-flask-line",
    body: "UK suppliers with demonstrated capability but no contract. A successful pilot, an innovation project, operational endorsement. We build the bridge between demonstration and procurement.",
  },
  {
    title: "Market entrants",
    icon: "ri-global-line",
    body: "Allied-nation technology firms entering the UK defence, security, or infrastructure market. We map the buyers, the compliance requirements, and the route to a credible first contract.",
  },
  {
    title: "Investors",
    icon: "ri-line-chart-line",
    body: "VCs, private equity, and family offices with portfolio companies in these markets. We assess contractability risk and identify what stands between proven technology and contracted revenue.",
  },
  {
    title: "Buyers",
    icon: "ri-government-line",
    body: "We work selectively with Government and critical infrastructure organisations who need to procure novel capability at pace.",
  },
];

const outcomes = [
  {
    audience: "Proven innovators",
    change: "Reach signed contracts, not more pilots, in months not years.",
  },
  {
    audience: "Market entrants",
    change: "Understand what the market actually requires and commit resources with confidence from week one.",
  },
  {
    audience: "Investors",
    change: "Create portfolio value by turning proven technology companies into contracted suppliers.",
  },
  {
    audience: "Buyers",
    change: "Attract stronger competition for their requirements and get better capability on contract.",
  },
];

export default function Offers() {
  const offersRef = useInView();
  const audienceRef = useInView();
  const changesRef = useInView();

  useEffect(() => {
    document.title = "Offers | Resecor Defence & National Security Advisory Services";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", "Three ways to engage Resecor: Assess and Orientate, Position and Pursue, or Sustain and Advance. Advisory services for technology suppliers entering UK defence, security, and resilience markets.");
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `${import.meta.env.VITE_SITE_URL ?? ""}/offers`);

    const scriptId = "jsonld-offers";
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/offers#webpage`,
          "url": `${import.meta.env.VITE_SITE_URL ?? ""}/offers`,
          "name": "Offers | Resecor Defence & National Security Advisory Services",
          "description": "Three ways to engage Resecor: Assess and Orientate, Position and Pursue, or Sustain and Advance.",
          "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/#website` }
        },
        {
          "@type": "ItemList",
          "name": "Resecor Advisory Offers",
          "description": "Advisory service packages for technology suppliers entering UK national security and resilience markets.",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "item": {
                "@type": "Service",
                "name": "Assess and Orientate",
                "description": "We assess whether a specific opportunity or market entry question is real, worth pursuing, and how to approach it. You get a decision pack and a practical plan.",
                "provider": { "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/#organization` },
                "areaServed": "GB"
              }
            },
            {
              "@type": "ListItem",
              "position": 2,
              "item": {
                "@type": "Service",
                "name": "Position and Pursue",
                "description": "We build the pursuit architecture: stakeholder mapping, competitive positioning, engagement sequencing, and a clear objective for every interaction.",
                "provider": { "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/#organization` },
                "areaServed": "GB"
              }
            },
            {
              "@type": "ListItem",
              "position": 3,
              "item": {
                "@type": "Service",
                "name": "Sustain and Advance",
                "description": "Monthly prioritisation, blocker resolution, stakeholder continuity, and decision support as a dedicated UK market advisory function.",
                "provider": { "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/#organization` },
                "areaServed": "GB"
              }
            }
          ]
        }
      ]
    });

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-navy font-body">
      <Navbar />

      {/* ── PAGE HERO ── */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/d46a45853ba1b89156f83ac0b7ec83a8.jpg"
            alt=""
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/80 to-navy" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <span className="font-heading text-xs tracking-[0.25em] uppercase text-copper font-medium">Offers</span>
          <h1 className="mt-4 font-heading font-bold text-3xl md:text-5xl text-white leading-tight max-w-2xl">
            3 ways to engage.
          </h1>
          <div className="mt-6 w-12 h-0.5 bg-copper" />
          <p className="mt-5 font-heading text-white/60 text-base md:text-lg tracking-wide">Start where you are.</p>
        </div>
      </section>

      {/* ── OFFER CARDS ── */}
      <section className="bg-navy pb-20 md:pb-28" ref={offersRef.ref}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 rounded-sm overflow-hidden">
            {offers.map((offer, i) => (
              <div
                key={offer.id}
                className={`relative bg-slate flex flex-col p-8 md:p-10 gap-5 group hover:bg-charcoal transition-all duration-500 ${
                  offersRef.inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                }`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                {/* Index */}
                <span className="font-heading text-5xl font-bold text-white/5 group-hover:text-copper/10 absolute top-6 right-8 transition-colors duration-300 leading-none select-none">
                  {offer.index}
                </span>
                {/* Icon */}
                <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-copper/10 group-hover:bg-copper/20 transition-colors duration-300">
                  <i className={`${offer.icon} text-copper text-lg`} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-xl text-white">{offer.title}</h3>
                  <p className="mt-2 font-body text-sm text-copper-light font-medium leading-snug">{offer.lead}</p>
                </div>
                <p className="font-body text-sm text-mid-grey leading-relaxed flex-1">{offer.body}</p>
                <div className="flex flex-col gap-4 mt-2">
                  <div className="flex items-center gap-2">
                    <i className="ri-time-line text-mid-grey/50 text-sm" />
                    <span className="font-body text-xs text-mid-grey/70">{offer.duration}</span>
                  </div>
                  <div className="w-full h-px bg-white/5" />
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 font-body text-sm text-copper hover:text-copper-light font-medium cursor-pointer whitespace-nowrap group/link"
                  >
                    Start a conversation
                    <i className="ri-arrow-right-line transition-transform duration-200 group-hover/link:translate-x-1" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO WE WORK WITH ── */}
      <section className="bg-slate py-20 md:py-28" ref={audienceRef.ref}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div
            className={`transition-all duration-700 ${audienceRef.inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <span className="font-heading text-xs tracking-[0.25em] uppercase text-copper font-medium">Clients</span>
            <h2 className="mt-3 font-heading font-bold text-2xl md:text-4xl text-white leading-tight max-w-md">
              Who we work with
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/5 rounded-sm overflow-hidden">
            {audiences.map((a, i) => (
              <div
                key={a.title}
                className={`bg-slate p-8 md:p-10 flex flex-col gap-4 group hover:bg-charcoal transition-all duration-500 ${
                  audienceRef.inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${i * 100 + 200}ms` }}
              >
                <div className="w-9 h-9 flex items-center justify-center">
                  <i className={`${a.icon} text-copper text-xl`} />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white">{a.title}</h3>
                <div className="w-6 h-px bg-copper/40 group-hover:w-12 group-hover:bg-copper transition-all duration-300" />
                <p className="font-body text-sm text-mid-grey leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT CHANGES ── */}
      <section className="bg-navy py-20 md:py-28" ref={changesRef.ref}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div
            className={`transition-all duration-700 ${changesRef.inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <span className="font-heading text-xs tracking-[0.25em] uppercase text-copper font-medium">Outcomes</span>
            <h2 className="mt-3 font-heading font-bold text-2xl md:text-4xl text-white leading-tight max-w-md">
              What changes
            </h2>
          </div>

          <div className="mt-12 flex flex-col divide-y divide-white/5">
            {outcomes.map((o, i) => (
              <div
                key={o.audience}
                className={`py-7 flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-6 group transition-all duration-500 ${
                  changesRef.inView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                }`}
                style={{ transitionDelay: `${i * 100 + 200}ms` }}
              >
                <span className="font-heading font-semibold text-base text-white whitespace-nowrap min-w-[160px] group-hover:text-copper transition-colors duration-200">
                  {o.audience}
                </span>
                <p className="font-body text-sm text-mid-grey leading-relaxed">
                  {o.change}
                </p>
              </div>
            ))}
          </div>

          <div
            className={`mt-14 transition-all duration-700 delay-500 ${changesRef.inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          >
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 px-8 py-4 bg-copper text-white hover:bg-copper-light transition-colors duration-200 font-body font-medium text-sm tracking-wide cursor-pointer whitespace-nowrap rounded-sm"
            >
              Start a Conversation
              <i className="ri-arrow-right-line text-base" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
